import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatNumber, formatPercent } from '@/i18n/locale';
import { MapPin } from 'lucide-react';

/**
 * Contact rate by phone zone.
 *
 * What changed on 2026-10-07, and why:
 *  - the bars were coloured on a six-step red-to-green scale whose legend did
 *    not even match the thresholds in the code (15-30 % in the legend, 20 and
 *    30 % in the code). One accent now marks the best zone, the rest is grey.
 *  - the bars were drawn on a 0-100 % scale, so contact rates (5 to 30 %)
 *    were slivers. They are now scaled to the best zone, with the average
 *    drawn as a tick on every bar.
 *  - the "average" was the plain mean of the zone rates: a zone with 2 calls
 *    weighed as much as a zone with 500. It is now reached calls over calls.
 *  - a second, unlabelled grey micro-bar (the volume) is gone: the volume is
 *    printed as a number.
 *  - zone names and units were hard-coded in French; they go through i18n.
 */

// French area code to zone key.
const AREA_CODES_FR: Record<string, string> = {
    '01': 'idf',
    '02': 'northWest',
    '03': 'northEast',
    '04': 'southEast',
    '05': 'southWest',
    '06': 'mobile',
    '07': 'mobile',
    '09': 'voip',
};

// International prefix to zone key.
const COUNTRY_PREFIXES: Record<string, string> = {
    '+33': 'france',
    '+32': 'belgium',
    '+41': 'switzerland',
    '+352': 'luxembourg',
    '+1': 'northAmerica',
    '+44': 'unitedKingdom',
    '+49': 'germany',
    '+34': 'spain',
    '+39': 'italy',
    '+351': 'portugal',
};

export function getRegionKey(phone: string | null | undefined): string {
    if (!phone) return 'unknown';
    const cleaned = phone.replace(/\s+/g, '');

    if (cleaned.startsWith('+33') || cleaned.startsWith('0033')) {
        const local = cleaned.startsWith('+33') ? '0' + cleaned.slice(3) : '0' + cleaned.slice(4);
        return AREA_CODES_FR[local.slice(0, 2)] || 'franceOther';
    }
    // A national number (06…), or its first digits only: the server groups
    // the calls by the first five characters of the number (W4).
    if (cleaned.startsWith('0') && !cleaned.startsWith('00')) {
        return AREA_CODES_FR[cleaned.slice(0, 2)] || 'franceOther';
    }
    // Longest prefix first, so +351 (Portugal) is not read as +35x.
    const prefixes = Object.keys(COUNTRY_PREFIXES).sort((a, b) => b.length - a.length);
    for (const prefix of prefixes) {
        if (cleaned.startsWith(prefix)) return COUNTRY_PREFIXES[prefix] ?? 'international';
    }
    return 'international';
}

interface ConnectRateHeatmapProps {
    /** Calls per first five characters of the prospect number (server, byPhonePrefix). */
    prefixes?: Array<{ prefix: string; total: number; connected: number; meetings: number }>;
}

interface RegionStat {
    key: string;
    total: number;
    connected: number;
    meetings: number;
    rate: number;
}

type SortMode = 'rate' | 'volume';

export default function ConnectRateHeatmap({ prefixes = [] }: ConnectRateHeatmapProps) {
    const { t } = useTranslation(['pages', 'common']);
    const [sortBy, setSortBy] = useState<SortMode>('rate');

    // Since W4 the counts come from the server, over every call of the period
    // (connected = reached the agent); here the prefixes are named as zones.
    const { regions, overall } = useMemo(() => {
        const stats: Record<string, { total: number; connected: number; meetings: number }> = {};
        let total = 0;
        let connected = 0;
        prefixes.forEach((p) => {
            const key = getRegionKey(p.prefix);
            if (!stats[key]) stats[key] = { total: 0, connected: 0, meetings: 0 };
            stats[key].total += p.total;
            stats[key].connected += p.connected;
            stats[key].meetings += p.meetings;
            total += p.total;
            connected += p.connected;
        });
        const list: RegionStat[] = Object.entries(stats)
            .map(([key, d]) => ({ key, ...d, rate: d.total > 0 ? (d.connected / d.total) * 100 : 0 }))
            .sort((a, b) => (sortBy === 'rate' ? b.rate - a.rate || b.total - a.total : b.total - a.total));
        return { regions: list, overall: total > 0 ? (connected / total) * 100 : 0 };
    }, [prefixes, sortBy]);

    const regionName = (key: string) => t(`pages:analytics.connectRateHeatmap.regions.${key}`);
    const maxRate = Math.max(...regions.map((r) => r.rate), 1);
    // The best zone is only named when it rests on enough calls to mean
    // something; otherwise one lucky call at 100 % would take the title.
    const best = [...regions].filter((r) => r.total >= 3).sort((a, b) => b.rate - a.rate)[0] || null;
    const avgLeft = `${Math.min(100, (overall / maxRate) * 100)}%`;

    return (
        <div className="bg-card rounded-xl border border-border overflow-hidden min-w-0">
            <div className="px-5 py-4 border-b border-border/50 flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2 min-w-0">
                    <MapPin className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--vf-text-3)' }} />
                    <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-foreground">
                            {best
                                ? t('pages:analytics.connectRateHeatmap.finding', { region: regionName(best.key), rate: formatPercent(best.rate) })
                                : t('pages:analytics.connectRateHeatmap.title')}
                        </h3>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--vf-text-3)' }}>
                            {best && <>{t('pages:analytics.connectRateHeatmap.title')}{' · '}</>}
                            {t('pages:analytics.connectRateHeatmap.average', { rate: formatPercent(overall) })}
                        </p>
                    </div>
                </div>
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortMode)}>
                    <SelectTrigger className="h-8 w-32 text-xs shrink-0" aria-label={t('pages:analytics.connectRateHeatmap.sortLabel')}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="rate">{t('pages:analytics.connectRateHeatmap.sortByRate')}</SelectItem>
                        <SelectItem value="volume">{t('pages:analytics.connectRateHeatmap.sortByVolume')}</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="p-5">
                {regions.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8 text-sm">{t('pages:analytics.connectRateHeatmap.empty')}</p>
                ) : (
                    <div className="space-y-3">
                        {regions.map((r) => {
                            const isBest = best !== null && r.key === best.key;
                            return (
                                <div key={r.key} className="grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)_3.5rem] sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)_3.5rem_5.5rem_4rem] items-center gap-x-3">
                                    <span className="text-sm text-foreground truncate" title={regionName(r.key)}>
                                        {regionName(r.key)}
                                    </span>
                                    <div className="relative h-2.5 rounded-full" style={{ background: 'var(--vf-surface-3)' }}>
                                        <div
                                            className="h-full rounded-full"
                                            style={{
                                                width: `${Math.max((r.rate / maxRate) * 100, r.rate > 0 ? 2 : 0)}%`,
                                                background: isBest ? 'var(--vf-accent)' : 'var(--vf-text-4)',
                                            }}
                                        />
                                        <span
                                            aria-hidden="true"
                                            className="absolute -top-1 -bottom-1 w-px"
                                            style={{ left: avgLeft, background: 'var(--vf-text-2)' }}
                                        />
                                    </div>
                                    <span
                                        className={`text-xs tabular-nums whitespace-nowrap text-right ${isBest ? 'font-semibold' : 'font-medium'}`}
                                        style={{ color: isBest ? 'var(--vf-accent)' : 'var(--vf-text-1)' }}
                                    >
                                        {formatPercent(r.rate)}
                                    </span>
                                    <span className="hidden sm:block text-xs tabular-nums whitespace-nowrap text-right" style={{ color: 'var(--vf-text-3)' }}>
                                        {formatNumber(r.total)} {t('common:entity.call', { count: r.total })}
                                    </span>
                                    <span className="hidden sm:block text-xs tabular-nums whitespace-nowrap text-right" style={{ color: 'var(--vf-text-3)' }}>
                                        {r.meetings > 0 ? `${formatNumber(r.meetings)} ${t('common:entity.meeting', { count: r.meetings })}` : ''}
                                    </span>
                                </div>
                            );
                        })}
                        <p className="flex items-center gap-2 pt-3 border-t border-border/50 text-[11px]" style={{ color: 'var(--vf-text-3)' }}>
                            <span className="inline-block w-px h-3" style={{ background: 'var(--vf-text-2)' }} />
                            {t('pages:analytics.connectRateHeatmap.legendAverage')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
